// First-party, anonymous product analytics.
// - Random install id only (no device fingerprinting, no PII).
// - Event names + a small allowlisted prop set. NEVER content:
//   journal entries, private conversations, reflections, protect list,
//   drafts, letters, mosaic — none of it is ever sent here.
// - Respects the user's opt-out (Settings).

import axios from "axios";
import { analyticsAllowed, setPrivacyChoice } from "@/lib/privacyConsent";
import { getInstallId } from "@/lib/installationId";

const API = process.env.REACT_APP_ANALYTICS_BACKEND_URL || "";
const OPTOUT_KEY = "un_analytics_optout_v1";
const LAST_OPEN_KEY = "un_last_open_v1";

const safeGet = (k) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const safeSet = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch (e) {
    console.warn("[analytics] set:", e?.message || e);
  }
};

export const isAnalyticsOptedOut = () => !analyticsAllowed() || safeGet(OPTOUT_KEY) === "1";
export const setAnalyticsOptOut = (out) => {
  safeSet(OPTOUT_KEY, out ? "1" : "0");
  setPrivacyChoice(out ? "essential" : "optional");
};

// Only these prop keys are ever transmitted.
const ALLOWED_PROPS = ["source", "state", "intervention", "plan", "rated", "type", "platform", "mode"];

let queue = [];
let flushTimer = null;

const flush = async () => {
  flushTimer = null;
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  try {
    await axios.post(`${API}/api/analytics/events`, {
      install_id: getInstallId(),
      events: batch,
    });
  } catch (e) {
    // Analytics must never disturb the user. Drop silently.
    console.warn("[analytics] flush:", e?.message || e);
  }
};

export const track = (name, props = {}) => {
  if (!API || isAnalyticsOptedOut()) return;
  const clean = {};
  Object.keys(props).forEach((k) => {
    if (ALLOWED_PROPS.includes(k) && props[k] != null) {
      clean[k] = String(props[k]).slice(0, 64);
    }
  });
  queue.push({ name, props: clean, ts: new Date().toISOString() });
  if (queue.length >= 10) {
    if (flushTimer) clearTimeout(flushTimer);
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, 2500);
  }
};

// Session tracking: app_opened once per tab session; return_session when
// the last open was more than 8 hours ago.
export const trackAppOpen = () => {
  try {
    if (sessionStorage.getItem("un_session_tracked")) return;
    sessionStorage.setItem("un_session_tracked", "1");
  } catch {
    return;
  }
  const last = Number(safeGet(LAST_OPEN_KEY) || 0);
  const now = Date.now();
  safeSet(LAST_OPEN_KEY, String(now));
  track("app_opened");
  if (last && now - last > 8 * 3600000) track("return_session");
};
